// Customers made by the product (#417): the platform lane, the MSP lane, and the
// team's own invitations.
//
// Read off `routes/customers.py` and `routes/invites.py`. Every creation carries
// a `creation_key` the form mints once when it opens, so a reload mid-submit
// or a second click replays the first result rather than making a second
// customer (`customers.py`); `replayed` in the answer says which happened.

import type { Api } from "./http";

export const ORGS_PATH = "/api/v1/orgs";
export const CUSTOMERS_PATH = "/api/v1/customers";
export const INVITES_PATH = "/invites";

export const CAPABILITIES = ["investigate", "propose", "approve_writes", "payload_reads", "scan"] as const;
export type Capability = (typeof CAPABILITIES)[number];

/** What each capability lets the organisation do, in the words the consent screen uses. */
export const CAPABILITY_TEXT: Record<Capability, { title: string; detail: string }> = {
  investigate: {
    title: "Investigate",
    detail: "Read this workspace's alerts, investigations and evidence, and start investigations on its accounts.",
  },
  propose: {
    title: "Propose changes",
    detail: "Its investigations may propose a change for the workspace to approve; without it, the conclusion carries the recommendation in prose.",
  },
  approve_writes: {
    title: "Approve changes",
    detail: "Its members answer approval cards for this workspace. Absent, the workspace's own members do.",
  },
  payload_reads: {
    title: "Ask to read contents",
    detail: "Its investigations may ask for consent to read a resource's contents. The answer always stays with the workspace.",
  },
  // #880: `posture:scan` on the visitor's lane, which its role must also carry.
  scan: {
    title: "Posture scans",
    detail: "Its owners and admins may start a posture scan of this workspace's AWS accounts, within the same limits as your own team.",
  },
};

export interface OrgRow {
  org_id: string;
  name: string;
  kind: "customer" | "partner";
  created_at: string;
  workspaces: number;
  members: number;
  live_invites: number;
  /** A customer's partners, by `partners_of` (#1195): empty for an independent customer. */
  managed_by?: PartnerRef[];
}

/** A partner as another's reading names it (#1195). `name` is null where the reader's lane may not read it. */
export interface PartnerRef {
  id: string;
  name: string | null;
}

export interface Created {
  org_id: string;
  kind: "customer" | "partner";
  name: string;
  workspace_id: string | null;
  workspace_name: string | null;
  invite: { id: string; email: string; expires_at: string };
  requested: Capability[];
  replayed: boolean;
}

export type GrantState = "pending" | "granted" | "declined" | "revoked";

export interface CustomerRow {
  workspace_id: string;
  name: string;
  /** The name of the customer that owns the workspace (#1195); null where it cannot be read. */
  customer_name?: string | null;
  grants: Partial<Record<Capability, GrantState>>;
  requested_at: string;
  may_enter: boolean;
  /** The customer's standing consent records, readable where the org may enter (#419). */
  consent_records?: number | null;
  /** Its AWS accounts, counted where the org may enter; null where it may not (#815). */
  accounts?: number | null;
  /** Its investigations still running or paused, where the org may enter (#815). */
  open_investigations?: number | null;
  /** Null for a workspace the org only pays for: paying opens no row of the workspace's (#632). */
  progress: {
    owner_accepted: boolean;
    account_connected: boolean;
    alert_source: boolean;
    first_run: boolean;
  } | null;
  /**
   * True when this organisation is the workspace's payer (#632, D6; #469 P3). A payer
   * is a flag and not a capability: it is on the book with its bill and no way in.
   */
  payer?: boolean;
  /**
   * True when the server takes an ask for capabilities here (#678): the org holds a grant
   * row, or a payer request the workspace accepted. An ended payer is still `payer` to the
   * period's end, and may not ask, so the book reads this rather than `payer`.
   */
  may_ask?: boolean;
  /** The workspace's line of the payer's bill for the running period, or null (#632). */
  bill?: {
    currency: string;
    credits_used: number;
    included_credits: number;
    overage_credits: number;
    fee: string;
    overage: string;
    total: string;
  } | null;
}

export interface InviteRow {
  id: string;
  email: string;
  role: string;
  invited_by: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
}

export const listOrgs = (api: Api): Promise<OrgRow[]> => api.get<OrgRow[]>(ORGS_PATH);

/** One workspace of an org, for the platform admin's "Enter" (#701). */
export interface OrgWorkspaceRow {
  workspace_id: string;
  name: string;
  created_at: string;
}

/** `GET /api/v1/orgs/{org_id}/workspaces`: the platform lane's alone; anyone else is refused. */
export const listOrgWorkspaces = (api: Api, orgId: string): Promise<OrgWorkspaceRow[]> =>
  api.get<OrgWorkspaceRow[]>(`${ORGS_PATH}/${encodeURIComponent(orgId)}/workspaces`);

export const createOrg = (
  api: Api,
  body: {
    kind: "customer" | "partner";
    name: string;
    owner_email: string;
    workspace_name?: string | null;
    creation_key: string;
  },
): Promise<Created> => api.post<Created>(ORGS_PATH, body);

export const listCustomers = (api: Api): Promise<CustomerRow[]> =>
  api.get<CustomerRow[]>(CUSTOMERS_PATH);

export const inviteCustomer = (
  api: Api,
  body: { name: string; admin_email: string; capabilities: Capability[]; creation_key: string },
): Promise<Created> => api.post<Created>(CUSTOMERS_PATH, body);

export const listInvites = (api: Api): Promise<InviteRow[]> => api.get<InviteRow[]>(INVITES_PATH);

export const inviteMember = (
  api: Api,
  body: { email: string; role: string },
): Promise<{ id: string; email: string; role: string; expires_at: string }> =>
  api.post(INVITES_PATH, body);

export const revokeInvite = (api: Api, inviteId: string): Promise<void> =>
  api.delete<void>(`${INVITES_PATH}/${encodeURIComponent(inviteId)}`);

/** A key for one form, minted when it opens: the browser's own, or a fallback for a test. */
export function creationKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `key-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
